/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
/* eslint-disable no-lonely-if */
//
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OfflineBoltOutlinedIcon from '@mui/icons-material/OfflineBoltOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { actionHelpers } from 'app-components/flux/core';
import { getStaticInstitutionFromAggregator } from 'app-components/flux/institutions/institutionsUtils';
import { rootUiActions } from 'app-components/flux/root-ui';
import { getLogger, tracker } from 'app-components/utils/core';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogWizardContent from 'components/Dialogs/StdDialogWizardContent';
import InstitutionIcon, { InstitutionIconSize } from 'components/InstitutionIcon';
import LinkPreview from 'components/LinkPreview';

import { institutionsActions, institutionsUtils } from 'app-components/flux/institutions';
import { showBillScoutDialog } from 'components/Dialogs/BillScout/actions';

import {
  accountDiscoveryActions,
  accountDiscoverySelectors,
  fiMigrationUtils,
} from 'app-components/flux/account-discovery';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import { preferencesV2Actions, preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { useFetchOnce } from 'app-components/hooks/utils';

import Dump from 'components/Dump';

import 'utils/interfaces/assert';

import AggActionIntro from './AggActionIntro';
import ConnectionUpdateIntro from './ConnectionUpdateIntro';
import ConnectionUpdateIcon from 'components/FiMigration/ConnectionUpdateIcon';
import { useFiMigrationPrompt } from 'app-components/hooks/accounts/useFiMigration';
import AppleWalletInstructions from './AppleWalletInstructions';
import Authenticator from './Authenticator';
import { getDefaultAggregatorPreference } from './defaultAggregator';
import FiIssueWarning from './FiIssueWarning';
import InstitutionAccountsUpdater from './InstitutionAccountsUpdater';
import InstitutionPicker from './InstitutionPicker';
import NonConnectedAccount from './NonConnectedAccount';
import NonConnectedAsset from './NonConnectedAsset';
import PartnerConsent from './PartnerConsent';
import PaymentProvider from './PaymentProvider';
import SendInstitutionRequest from './SendInstitutionRequest';
import WidgetAuthenticator from './WidgetAuthenticator';

const log = getLogger('AccountDiscovery');

const DIRECT_ADD_SELECTIONS = {
  'ADD-FI-ASSETS': 'add-asset',
  'ADD-FI-MANUAL': 'add-manual',
};

function getParamsBasedOnMode(mode, aggregator, institutionLogin, customDialogTitle) {
  switch (mode) {
    case 'ACCOUNT-REDISCOVERY':
      return {
        cpSetupMode:
          aggregator !== 'FINICITY' && aggregator !== 'PLAID'
            ? 'DISCOVER_ACCOUNTS_ONLY'
            : 'DISCOVER_AND_REPLACE_ACCOUNTS',
        fullAddAccountsDialog: true,
        dialogTitle: customDialogTitle || 'Reconnect account',
        page: verify(institutionLogin?.institutionId, 'institutionLogin missing') ? 'AUTHENTICATOR' : 'SEARCH',
      };
    case 'ADD-FI-ASSETS':
      return {
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        fullAddAccountsDialog: true,
        dialogTitle: 'Track assets',
        page: 'ADD_NON_CONNECTED_ASSET',
      };
    case 'ADD-FI-MANUAL':
      return {
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        fullAddAccountsDialog: true,
        dialogTitle: customDialogTitle || 'Add manual account',
        page: 'ADD_NON_CONNECTED_ACCOUNT',
      };
    case 'ADD-PAYMENT-PROVIDER':
      return {
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        fullAddAccountsDialog: true,
        dialogTitle: customDialogTitle || 'Accept online payments',
        page: 'ADD_PAYMENT_PROVIDER',
      };
    case 'MIGRATE-INSTITUTION-LOGIN':
      return {
        cpSetupMode: 'DISCOVER_AND_ADD_ACCOUNTS',
        fullAddAccountsDialog: true,
        dialogTitle: customDialogTitle || 'Re-Authenticate',
        page: 'AUTHENTICATOR',
      };
    case 'PROVIDE-CREDENTIALS': // TODO: This is going away with EWC-Live
      return {
        cpSetupMode: 'PROVIDE_CREDENTIALS',
        fullAddAccountsDialog: false,
        dialogTitle: customDialogTitle || 'Provide credentials',
        page: 'AUTHENTICATOR',
      };
    case 'PROVIDE-MFA':
      return {
        cpSetupMode: 'PROVIDE-MFA',
        fullAddAccountsDialog: true,
        dialogTitle: customDialogTitle || 'Multi-factor authentication',
        page: 'AUTHENTICATOR',
      };
    case 'RE-AUTHENTICATE':
      return {
        cpSetupMode: 'UPDATE_CREDENTIALS',
        fullAddAccountsDialog: false,
        dialogTitle: customDialogTitle || 'Re-Authenticate',
        page: 'AUTHENTICATOR',
      };
    case 'UPDATE-CREDENTIALS':
      return {
        cpSetupMode: 'UPDATE_CREDENTIALS',
        fullAddAccountsDialog: false,
        dialogTitle: customDialogTitle || 'Update credentials',
        page: 'AUTHENTICATOR',
      };
    case 'ADD-FI':
    case 'UPDATE-FI':
    case 'GSF':
    default:
      return {
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        fullAddAccountsDialog: true,
        dialogTitle: customDialogTitle || 'Add account',
        page: 'SEARCH',
      };
  }
}

function getInitialState(mode, institutionLogin, mfaChallenge, customDialogTitle) {
  let aggregator = null;
  if (mode === 'ADD-FI' || mode === 'GSF' || (mode === 'UPDATE-FI' && !institutionLogin?.institutionId)) {
    if (mode !== 'UPDATE-FI' && institutionLogin) {
      log.warn('The institutionLogin is ignored when in ADD mode');
      institutionLogin = null; // eslint-disable-line no-param-reassign
    }
  } else if (mode === 'ADD-PAYMENT-PROVIDER') {
    aggregator = 'STRIPE';
  } else if (!institutionLogin) {
    assert(true, 'The institutionLogin is required when not in ADD mode');
  } else if (mode === 'MIGRATE-INSTITUTION-LOGIN' && institutionLogin.fiMigrationInfo) {
    aggregator = institutionsUtils.getAggregatorFromChannel(institutionLogin.fiMigrationInfo.toChannel);
  } else {
    aggregator = institutionsUtils.getAggregatorFromChannel(institutionLogin.channel);
  }
  if (mode !== 'PROVIDE-MFA' && mfaChallenge != null) {
    log.warn('The mfaChallenge is ignored when not in PROVIDE-MFA mode');
    mfaChallenge = null; // eslint-disable-line no-param-reassign
  }

  const paramsBasedOnMode = getParamsBasedOnMode(mode, aggregator, institutionLogin, customDialogTitle);
  return {
    aggregator,
    mode,
    cpSetupMode: paramsBasedOnMode.cpSetupMode,
    fullAddAccountsDialog: paramsBasedOnMode.fullAddAccountsDialog,
    institutionLogin,
    page: paramsBasedOnMode.page,
    dialogTitle: paramsBasedOnMode.dialogTitle,
    mfaChallenge,
  };
}

const AccountDiscoveryWizard = (props) => {
  const {
    afterCloseAction,
    aggAction,
    aggActionShowIntro,
    onClose,
    closeOnBack = false,
    dialogId,
    embeddedSearch = false,
    embeddedSearchFullWidth = false,
    initiator = 'unknown',
    additionalTrackingProperties,
    customDialogTitle,
    businessId,
    mode: modeProp = 'ADD_FI',
    institutionLogin: institutionLoginProp = null,
    institutionId: institutionIdProp = null,
  } = props;
  const dispatch = useDispatch();

  const featureFlags = useSelector(featureFlagsSelectors.getFeatureFlags);

  const addFiDefaultProviderFF = featureFlags.get('addFiDefaultProvider');
  const fiStatusEnabled = featureFlags.get('fiStatus');
  const fiAggregatorPreference = useSelector((state) =>
    preferencesV2Selectors.getPreferenceValue(state, 'fi-aggregator'),
  );

  const isAuthenticationDone = useSelector((state) =>
    accountDiscoverySelectors.getIsAuthenticationDone(state, { scope: dialogId }),
  );
  const isAccountUpsertingDone = useSelector((state) =>
    accountDiscoverySelectors.getIsAccountUpsertingDone(state, { scope: dialogId }),
  );

  const accountDiscoveryData = useSelector((state) =>
    accountDiscoverySelectors.getAccountDiscoveryData(state, { scope: dialogId }),
  );
  const accountDiscoveryTrackingProps = useMemo(
    () => ({
      updating: Boolean(accountDiscoveryData?.activeAccounts?.size || accountDiscoveryData?.deadAccounts?.size),
      activeAccounts: accountDiscoveryData?.activeAccounts?.size,
      deadAccounts: accountDiscoveryData?.deadAccounts?.size,
      newAccounts: accountDiscoveryData?.newAccounts?.size,
      unconnectedAccounts: accountDiscoveryData?.unconnectedAccounts?.size,
    }),
    [accountDiscoveryData],
  );

  const institution = useSelector((state) => accountDiscoverySelectors.getInstitution(state, { scope: dialogId }));
  const channelData = useSelector((state) =>
    accountDiscoverySelectors.getInstitutionChannelData(state, { scope: dialogId }),
  );
  const institutionLogin = useSelector((state) =>
    accountDiscoverySelectors.getInstitutionLogin(state, { scope: dialogId }),
  );

  const initialState = useMemo(
    () => getInitialState(modeProp, institutionLoginProp, props.mfaChallenge, customDialogTitle),
    [modeProp, institutionLoginProp, props.mfaChallenge, customDialogTitle],
  );

  const [mode, setMode] = useState(initialState.mode);
  const migrationAggregator = fiMigrationUtils.getFiMigrationAggregator({
    mode,
    institutionLogin: institutionLoginProp,
  });

  const aggregatorPreference = getDefaultAggregatorPreference(addFiDefaultProviderFF, fiAggregatorPreference);

  // Use the aggregator from: 1.institution login OR 2.institution OR 3.prefV2/LD.
  const aggregator = migrationAggregator || initialState.aggregator || institution?.aggregator || aggregatorPreference;

  const [initialPage] = useState(initialState.page);
  const [addedAccountClientIds, setAddedAccountClientIds] = useState(null);
  const [cpSetupMode, setCpSetupMode] = useState(initialState.cpSetupMode);
  const [dialogTitle, setDialogTitle] = useState(initialState.dialogTitle);
  const [fullAddAccountsDialog, setFullAddAccountsDialog] = useState(initialState.fullAddAccountsDialog);
  const [isClosing, setIsClosing] = useState(false);
  const [page, setPage] = useState(initialState.page);
  const isMigrationIntro = page === 'AGG_ACTION_INTRO' && fiMigrationUtils.isFiMigrationAction(aggAction);
  const [defaultVals, setDefaultVals] = useState();

  const pageRef = useRef();
  const autoMigrationRef = useRef(false);
  const migrationStartTracked = useRef(false);
  const migrationIntroDismissed = useRef(false);
  const trackMigrationIntroPrompt = useFiMigrationPrompt({
    institutionLogin: initialState.institutionLogin,
    surface: 'intro modal',
    location: 'account discovery',
    visible: false,
  });
  const theme = useTheme();

  const fixFiMode = useMemo(
    () => !['ADD-FI', 'ADD-FI-ASSETS', 'ADD-FI-MANUAL', 'GSF'].includes(mode) && (mode !== 'UPDATE-FI' || aggAction),
    [aggAction, mode],
  );
  const fiIconSize = useMemo(() => InstitutionIconSize(institution, 'md'), [institution]);

  const trackingProperties = useMemo(
    () => ({
      ...additionalTrackingProperties,
      ...(aggAction && {
        agg_action_type: aggAction.type,
        agg_action_id: aggAction.id,
        agg_status: aggAction.status,
        agg_status_code: aggAction.statusCode,
      }),
      aggregator,
      initiator,
      ...institutionsUtils.getInstitutionTrackingProperties({ institution }),
      ...(institution && {
        channel: institution.fdp?.channelData?.type,
        aggregator: migrationAggregator ? migrationAggregator : institution.aggregator,
      }),
      mode,
      ...fiMigrationUtils.getFiMigrationFunnelProperties({
        mode,
        institutionLogin: institutionLoginProp,
        aggAction,
        autoMigration: autoMigrationRef.current,
      }),
    }),
    [
      additionalTrackingProperties,
      aggAction,
      aggregator,
      initiator,
      institution,
      institutionLoginProp,
      migrationAggregator,
      mode,
    ],
  );

  const manualTrackingProperties = useMemo(
    () => ({
      ...trackingProperties,
      institution_id: '0',
      institution_name: 'manual',
    }),
    [trackingProperties],
  );

  const trackNonConnectedSelection = useCallback(
    (selectedBy) => {
      tracker.track(!fixFiMode ? tracker.events.addFISelect : tracker.events.fixFISelect, {
        ...(aggAction && {
          agg_action_type: aggAction.type,
          agg_action_id: aggAction.id,
          agg_status: aggAction.status,
          agg_status_code: aggAction.statusCode,
        }),
        selected_by: selectedBy,
        ...manualTrackingProperties,
      });
    },
    [aggAction, fixFiMode, manualTrackingProperties],
  );
  const trackNonConnectedSelectionRef = useRef(trackNonConnectedSelection);
  trackNonConnectedSelectionRef.current = trackNonConnectedSelection;
  const hasTrackedDirectSelectionRef = useRef(false);

  // initialize state including store state in redux
  const initializeOrReinitializeState = useCallback(
    async (onBack = false) => {
      // reset local state values
      setCpSetupMode(initialState.cpSetupMode);
      setDialogTitle(
        fiMigrationUtils.isFiMigrationAction(aggAction)
          ? 'Update connection'
          : !aggAction
            ? initialState.dialogTitle
            : aggAction.title,
      );
      setFullAddAccountsDialog(initialState.fullAddAccountsDialog);
      setPage(
        aggActionShowIntro || fiMigrationUtils.isFiMigrationAction(aggAction) ? 'AGG_ACTION_INTRO' : initialState.page,
      );

      let effectiveMode = initialState.mode;
      let institutionId;
      let channel;
      let institutionLoginIdBeingMigrated;
      let institutionLoginToKeep = initialState.institutionLogin;
      if (mode === 'MIGRATE-INSTITUTION-LOGIN' && initialState.institutionLogin.fiMigrationInfo) {
        institutionId = initialState.institutionLogin.fiMigrationInfo.toInstitutionId;
        // only use explicit channel during migration state
        channel = initialState.institutionLogin.fiMigrationInfo.toChannel;
        institutionLoginIdBeingMigrated = initialState.institutionLogin.id;
        institutionLoginToKeep = null;
      } else {
        // Do not reapply preselection after Back or the picker is skipped again.
        institutionId = initialState.institutionLogin?.institutionId || (onBack ? undefined : institutionIdProp);
      }

      let newInstitution;
      if (institutionId) {
        const fetchInstitution = actionHelpers.bindPromiseAction(dispatch, institutionsActions.getInstitution);
        newInstitution = (await fetchInstitution({ id: institutionId }))?.resource;
        // if not explicit migration state, use default channel from fresh institution from QCS
        channel = channel || institutionsUtils.getDefaultChannel(newInstitution, featureFlags);

        // if channel has changed to a new default, and migration is active to that channel
        if (
          modeProp !== 'PROVIDE-MFA' && // don't migrate if just doing an MFA
          initialState.institutionLogin?.fiMigrationInfo &&
          channel !== initialState.institutionLogin.channel &&
          channel === initialState.institutionLogin.fiMigrationInfo.toChannel
        ) {
          log.debug('New default channel found while in error', 'Auto-migrating to new channel');
          // use the new migrated institution
          newInstitution =
            (await fetchInstitution({ id: initialState.institutionLogin.fiMigrationInfo.toInstitutionId }))?.resource ||
            newInstitution;

          // update state values to pretend like we have always been migrating
          effectiveMode = 'MIGRATE-INSTITUTION-LOGIN';
          autoMigrationRef.current = initialState.mode !== 'MIGRATE-INSTITUTION-LOGIN';
          setMode('MIGRATE-INSTITUTION-LOGIN');
          setCpSetupMode('DISCOVER_AND_ADD_ACCOUNTS');
          setFullAddAccountsDialog(true);
          institutionLoginIdBeingMigrated = initialState.institutionLogin.id;
          institutionLoginToKeep = null;
        }
      } else if (mode === 'ADD-PAYMENT-PROVIDER') {
        newInstitution = getStaticInstitutionFromAggregator(aggregator);
        channel = institutionsUtils.getDefaultChannel(newInstitution, featureFlags);
      }

      dispatch(
        accountDiscoveryActions.accountDiscoverySetup(
          {
            institutionLogin: institutionLoginToKeep,
            mfaChallenge: initialState.mfaChallenge,
            institutionLoginIdBeingMigrated,
            institution: newInstitution,
            channel,
          },
          { scope: dialogId },
        ),
      );

      if ((!onBack || mode === 'ADD-PAYMENT-PROVIDER') && newInstitution) {
        dispatch(
          accountDiscoveryActions.institutionSelected(
            {
              institution: newInstitution,
              channel,
            },
            { scope: dialogId },
          ),
        );
      }

      const isMigration = effectiveMode === 'MIGRATE-INSTITUTION-LOGIN';
      if (isMigration && migrationStartTracked.current) {
        return;
      }
      migrationStartTracked.current = isMigration;
      tracker.track(isMigration || fixFiMode ? tracker.events.fixFIStart : tracker.events.addFIStart, {
        ...additionalTrackingProperties,
        ...(aggAction && {
          agg_action_type: aggAction.type,
          agg_action_id: aggAction.id,
          agg_status: aggAction.status,
          agg_status_code: aggAction.statusCode,
        }),
        // Add FI may know the connection already; never attribute its routing default.
        ...((fixFiMode || initialState.aggregator) && {
          aggregator: initialState.aggregator,
        }),
        initiator,
        ...(newInstitution &&
          isMigration && {
            ...institutionsUtils.getInstitutionTrackingProperties({ institution: newInstitution }),
            aggregator:
              fiMigrationUtils.getFiMigrationAggregator({
                mode: effectiveMode,
                institutionLogin: initialState.institutionLogin,
              }) || newInstitution.aggregator,
          }),
        mode: effectiveMode,
        ...fiMigrationUtils.getFiMigrationFunnelProperties({
          mode: effectiveMode,
          institutionLogin: initialState.institutionLogin,
          aggAction,
          autoMigration: autoMigrationRef.current,
        }),
      });

      const directSelection = DIRECT_ADD_SELECTIONS[mode];
      if (!onBack && directSelection && !hasTrackedDirectSelectionRef.current) {
        hasTrackedDirectSelectionRef.current = true;
        trackNonConnectedSelectionRef.current(directSelection);
      }
    }, // don't re-init state on aggregator change
    // every time this callback regenerates it re-inits state. Manage dependencies carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fixFiMode,
      aggAction,
      aggActionShowIntro,
      dialogId,
      dispatch,
      featureFlags,
      initialState,
      initiator,
      institutionIdProp,
      modeProp,
    ],
  );

  useFetchOnce(preferencesV2Actions.getPreferencesV2, preferencesV2Selectors, true);

  // initialize or re-initialize state (including redux) upon component mount (or when props change, though shouldn't happen)
  useEffect(() => {
    initializeOrReinitializeState();
    return () => dispatch(accountDiscoveryActions.accountDiscoveryCleanup(null, { scope: dialogId }));
  }, [dialogId, dispatch, initializeOrReinitializeState]);

  const handleAddNonConnectedAccount = (defaultValues) => {
    setDefaultVals(defaultValues);
    setDialogTitle('Add manual account');
    setPage('ADD_NON_CONNECTED_ACCOUNT');
    trackNonConnectedSelection('add-manual');
  };

  const handlePartnerConsent = () => {
    setPage('AUTHENTICATOR');
  };

  const handlePaymentProviderContinue = () => {
    setPage('AUTHENTICATOR');
  };

  const handleAddNonConnectedAsset = () => {
    setDialogTitle('Track assets');
    setPage('ADD_NON_CONNECTED_ASSET');
    trackNonConnectedSelection('add-asset');
  };

  const handleAddAppleWallet = () => {
    setPage('APPLE_WALLET_INSTRUCTIONS');
  };

  const handleAggActionSubmit = () => {
    setPage(initialPage);
  };

  const handleOpenInstitutionRequest = () => {
    setDialogTitle('Send request');
    setPage('SEND_INSTITUTION_REQUEST');
    tracker.track(tracker.events.requestFISelected, trackingProperties);
  };

  const handleSendInstitutionRequest = ({ institutionName, loginURL }) => {
    tracker.track(tracker.events.requestFISubmit, {
      ...trackingProperties,
      institution_id: '0',
      institution_name: institutionName,
      institution_url: loginURL,
    });
  };

  const handleClose = (e, reason) => {
    if (pageRef.current?.handleClose) {
      pageRef.current.handleClose(reason);
    }

    if (reason !== 'SUBMIT_SUCCESS') {
      if (isMigrationIntro && !migrationIntroDismissed.current) {
        migrationIntroDismissed.current = true;
        trackMigrationIntroPrompt('dismissed');
      }
      tracker.track(!fixFiMode ? tracker.events.addFICancel : tracker.events.fixFICancel, {
        ...trackingProperties,
        step: page.toLowerCase(),
        ...(aggAction && {
          agg_action_type: aggAction.type,
          agg_action_id: aggAction.id,
          agg_status: aggAction.status,
          agg_status_code: aggAction.statusCode,
        }),
      });
    }
    setIsClosing(true);

    if (reason === 'SUBMIT_SUCCESS' && afterCloseAction) {
      dispatch(afterCloseAction);
    }
    onClose?.();
    setTimeout(() => dispatch(rootUiActions.removeDialog(dialogId)), 2000);
  };

  const handleCancel = () => {
    if (
      (mode === 'ADD-FI' || mode === 'ADD-PAYMENT-PROVIDER' || mode === 'GSF' || mode === 'UPDATE-FI') &&
      page !== 'SEARCH' &&
      page !== 'BILL_SCOUT'
    ) {
      tracker.track(!fixFiMode ? tracker.events.addFICancel : tracker.events.fixFICancel, {
        ...trackingProperties,
        step: page.toLowerCase(),
        reinitializing: true,
        ...(aggAction && {
          agg_action_type: aggAction.type,
          agg_action_id: aggAction.id,
          agg_status: aggAction.status,
          agg_status_code: aggAction.statusCode,
        }),
      });
      initializeOrReinitializeState(true);
    } else {
      handleClose(null, 'CANCELLED');
    }
  };

  const handleBackOrCancel = () => {
    if (closeOnBack) {
      handleClose(null, 'CANCELLED');
    } else if (pageRef.current?.handleBack) {
      pageRef.current.handleBack();
    } else {
      handleCancel();
    }
  };

  const handlePageClose = () => {
    handleClose(null, 'CANCELLED');
  };

  const handleFinishLater = () => {
    handleClose(null, 'SUBMIT_SUCCESS');
  };

  const handleReset = () => initializeOrReinitializeState();

  // TODO: bad, fix
  switch (page) {
    case 'SEARCH':
      if (institution && aggregator === 'FDP') {
        log.debug('AccountDiscovery switching to CONSENT page', institution);
        // TODO: title should be totally abstracted from 'page'
        setDialogTitle('Add account');
        // Determines if we need to send user through stepped issue dialog alerting them of connection issues.
        const showSteppedFiIssueWarning =
          institution.fiIssue && fiStatusEnabled && channelData?.authType === 'PARTNER_AUTH';
        setPage(showSteppedFiIssueWarning ? 'FI_ISSUE' : 'FDP_CONSENT');
      } else if (institution) {
        log.debug('AccountDiscovery switching to AUTHENTICATOR page', institution);
        setPage('AUTHENTICATOR');
      }
      break;
    case 'AUTHENTICATOR':
      if (isAuthenticationDone) {
        if (accountDiscoveryData) {
          log.debug('AccountDiscovery switching to ACCOUNTS page', accountDiscoveryData);
          setPage('ACCOUNTS');
          setDialogTitle('Add account');

          // We track authenticator completed here instead of in the authenticator cause completion isn't done until
          // QCS finishes and returns accountDiscoveryData (in which case - authenticator may never get control again).
          //
          tracker.track(!fixFiMode ? tracker.events.addFIAuthenticated : tracker.events.fixFIAuthenticated, {
            ...trackingProperties,
            ...accountDiscoveryTrackingProps,
          });
        } else if (!isClosing) {
          // We should only get here in update mode (fixing FI). We track authenticator completed here instead of in the
          // authenticator cause completion isn't done until QCS finishes - authenticator may never get control again).
          //
          tracker.track(tracker.events.fixFIAuthenticated, {
            ...trackingProperties,
            ...accountDiscoveryTrackingProps,
          });

          // We also need to send the complete event cause we are done (will not display the accounts updater page)
          //
          tracker.track(tracker.events.fixFIComplete, {
            ...trackingProperties,
            ...accountDiscoveryTrackingProps,
          });

          handleClose(null, 'SUBMIT_SUCCESS');
        }
      }
      break;
    case 'ACCOUNTS':
      if (isAccountUpsertingDone) {
        if (mode === 'ADD-FI') {
          if (!isClosing) {
            if (institutionLogin?.id && addedAccountClientIds.length) {
              dispatch(showBillScoutDialog(institutionLogin.id, addedAccountClientIds));
            }
            setIsClosing(true);
            setTimeout(() => handleClose(null, 'SUBMIT_SUCCESS'), 80);
          }
        } else if (!isClosing) {
          handleClose(null, 'SUBMIT_SUCCESS');
        }
      }
      break;
    case 'ADD_NON_CONNECTED_ACCOUNT':
      if (isAccountUpsertingDone) {
        if (!isClosing) {
          handleClose(null, 'SUBMIT_SUCCESS');
        }
      }
      break;
    default:
      break;
  }

  // if we are in the process of closing, do not render anything
  if (isClosing) {
    return null;
  }

  const renderAggActionIntro = () =>
    fiMigrationUtils.isFiMigrationAction(aggAction) && initialState.institutionLogin ? (
      <ConnectionUpdateIntro institutionLogin={initialState.institutionLogin} onSubmit={handleAggActionSubmit} />
    ) : (
      <AggActionIntro
        aggAction={aggAction}
        fiBrandingIndent={fiIconSize.width + 2 * parseInt(theme.spacing(3), 10)}
        onSubmit={handleAggActionSubmit}
      />
    );

  const renderSearch = () => (
    <InstitutionPicker
      aggregator={aggregator}
      onAddUnconnectedAccount={handleAddNonConnectedAccount}
      onAddUnconnectedAsset={handleAddNonConnectedAsset}
      onAddAppleWallet={handleAddAppleWallet}
      onOpenInstitutionRequest={handleOpenInstitutionRequest}
      scope={dialogId}
      submitButtonLabel='Continue'
      trackingProperties={trackingProperties}
      fixFiMode={fixFiMode}
      onFinishLater={handleFinishLater}
      flushContentPadding={embeddedSearchFullWidth}
      useGsfOptionIcons={mode === 'GSF'}
    />
  );

  const renderPartnerConsent = () => (
    <PartnerConsent
      institution={institution}
      handlePartnerConsent={handlePartnerConsent}
      trackingProperties={trackingProperties}
    />
  );

  const handleIssueTryAnyway = () => {
    setPage('FDP_CONSENT');
  };

  const renderFiIssueDialog = () => (
    <FiIssueWarning
      institution={institution}
      handleTryAnywayPress={handleIssueTryAnyway}
      handleCancel={handleCancel}
      trackingProperties={trackingProperties}
    />
  );

  const renderAppleWalletInstructions = () => <AppleWalletInstructions handleGotItClick={handleBackOrCancel} />;

  const renderAuthenticator = () => {
    if (aggregator === 'FINICITY' || aggregator === 'PLAID' || aggregator === 'STRIPE') {
      if (institution) {
        return (
          <WidgetAuthenticator
            aggregator={aggregator}
            fiBrandingIndent={fiIconSize.width + 2 * parseInt(theme.spacing(3), 10)}
            institution={institution}
            institutionLoginToUpdate={initialState.institutionLogin}
            onBack={handleCancel}
            reauthenticate={cpSetupMode === 'UPDATE_CREDENTIALS'}
            ref={pageRef}
            scope={dialogId}
            trackingProperties={trackingProperties}
            fixFiMode={fixFiMode}
            businessId={businessId}
          />
        );
      }
    } else if (institution) {
      return (
        <Authenticator
          aggregator={aggregator}
          cpSetupMode={cpSetupMode}
          fiBrandingIndent={fiIconSize.width + 2 * parseInt(theme.spacing(3), 10)}
          fullAddAccountsDialog={fullAddAccountsDialog}
          handleClose={handlePageClose}
          hideInitialPartnerAuthIntro={!!aggActionShowIntro}
          institution={institution}
          onCancel={handleBackOrCancel}
          provideForRefreshAccounts={mode === 'PROVIDE-CREDENTIALS' || mode === 'PROVIDE-MFA'}
          ref={pageRef}
          scope={dialogId}
          trackingProperties={trackingProperties}
          fixFiMode={fixFiMode}
        />
      );
    }
    return (
      <Box display='flex' minHeight='200px'>
        <CircularProgress id='authenticator-progress' size={100} style={{ margin: 'auto' }} />
      </Box>
    );
  };

  const renderAccounts = () => {
    if (institution && accountDiscoveryData) {
      return (
        <InstitutionAccountsUpdater
          cancelButtonLabel='Cancel'
          disconnect={mode === 'MIGRATE-INSTITUTION-LOGIN' ? true : undefined}
          onCancel={handleBackOrCancel}
          onClose={handlePageClose}
          onReset={handleReset}
          onSubmit={setAddedAccountClientIds}
          addIgnoredAccounts={aggregator === 'FINICITY' || aggregator === 'PLAID'}
          scope={dialogId}
          submitButtonLabel='Submit'
          trackingProperties={trackingProperties}
          fixFiMode={fixFiMode}
        />
      );
    }
    return (
      <Box display='flex' minHeight='200px'>
        <CircularProgress size={100} style={{ margin: 'auto' }} />
      </Box>
    );
  };

  const renderFiBranding = () => {
    const AggActionIcon = aggAction && (aggAction.type === 'ERROR' ? ErrorOutlineIcon : OfflineBoltOutlinedIcon);
    return (
      <Box display='flex'>
        <Box mt={3} mx={3} sx={{ position: 'relative' }}>
          <InstitutionIcon aggregator={aggregator} institution={institution} size='md' />
          {mode === 'MIGRATE-INSTITUTION-LOGIN' ? (
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 'full',
                boxShadow: 13,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                right: -8,
                top: 40,
              }}
            >
              <ConnectionUpdateIcon size={22} />
            </Box>
          ) : (
            AggActionIcon && (
              <AggActionIcon
                sx={{
                  backgroundColor: 'white',
                  border: 'solid 1px lightgrey',
                  borderRadius: '50%',
                  color: aggAction.type === 'ERROR' ? 'error.main' : 'warning.main',
                  display: 'flex',
                  ml: 'auto',
                  position: 'relative',
                  right: '-8px',
                  top: '-16px',
                  zIndex: '10',
                }}
              />
            )
          )}
        </Box>
        <Box display='flex' flexDirection='column' mt={3}>
          <Dump value={institution} />
          <Typography data-testid='institution-name'>{institution?.name}</Typography>
          {fullAddAccountsDialog && <LinkPreview href={institution?.url || ''} showFavicon={false} />}
        </Box>
      </Box>
    );
  };

  const showFiBranding =
    !isMigrationIntro &&
    page !== 'ADD_NON_CONNECTED_ACCOUNT' &&
    page !== 'ADD_NON_CONNECTED_ASSET' &&
    page !== 'APPLE_WALLET_INSTRUCTIONS' &&
    page !== 'ADD_PAYMENT_PROVIDER' &&
    (page !== 'AUTHENTICATOR' || aggregator !== 'STRIPE') &&
    page !== 'FDP_CONSENT' &&
    page !== 'SEARCH' &&
    page !== 'SEND_INSTITUTION_REQUEST';

  // Only the search page is embedded; later wizard pages still use the dialog.
  const inlineSearch = embeddedSearch && page === 'SEARCH';

  const wizardContent = (
    <StdDialogWizardContent
      minHeight={inlineSearch || isMigrationIntro ? undefined : fullAddAccountsDialog ? '550px' : '400px'}
    >
      {showFiBranding && renderFiBranding()}
      {page === 'AGG_ACTION_INTRO' && renderAggActionIntro()}
      {page === 'SEARCH' && renderSearch()}
      {page === 'FDP_CONSENT' && renderPartnerConsent()}
      {page === 'AUTHENTICATOR' && renderAuthenticator()}
      {page === 'ACCOUNTS' && renderAccounts()}
      {page === 'FI_ISSUE' && renderFiIssueDialog()}
      {page === 'APPLE_WALLET_INSTRUCTIONS' && renderAppleWalletInstructions()}
      {page === 'ADD_NON_CONNECTED_ACCOUNT' && (
        <NonConnectedAccount
          scope={dialogId}
          defaultVals={defaultVals}
          trackingProperties={manualTrackingProperties}
          onClose={handleClose}
        />
      )}
      {page === 'ADD_NON_CONNECTED_ASSET' && (
        <NonConnectedAsset
          setPage={setPage}
          setDefaultVals={setDefaultVals}
          cancelButtonLabel='Back'
          onClose={handleClose}
          trackingProperties={trackingProperties}
        />
      )}
      {page === 'SEND_INSTITUTION_REQUEST' && (
        <SendInstitutionRequest handleReset={handleReset} onSendInstitutionRequest={handleSendInstitutionRequest} />
      )}
      {page === 'ADD_PAYMENT_PROVIDER' && <PaymentProvider onContinue={handlePaymentProviderContinue} />}
    </StdDialogWizardContent>
  );

  if (inlineSearch) {
    return wizardContent;
  }

  return (
    <StdDialog
      dialogId={dialogId}
      disableBackdropClick={fullAddAccountsDialog}
      disableEnforceFocus
      disableEscapeKeyDown={fullAddAccountsDialog}
      hideBackdrop={mode === 'GSF'}
      onBack={handleBackOrCancel}
      onClose={handleClose}
      open={!isClosing}
      showBackButton={
        fullAddAccountsDialog &&
        page !== 'ADD_PAYMENT_PROVIDER' &&
        page !== 'AGG_ACTION_INTRO' &&
        page !== 'SEARCH' &&
        mode !== 'ADD-FI-MANUAL'
      }
      showCloseButton
      size={isMigrationIntro ? 'custom' : fullAddAccountsDialog ? 'md' : 'xs'}
      {...(isMigrationIntro && { PaperComponent: Paper })}
      paperProps={
        isMigrationIntro
          ? {
              sx: {
                width: 520,
                maxWidth: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
                m: { xs: 2, sm: 4 },
                borderRadius: '3xl',
              },
            }
          : undefined
      }
      titleProps={
        isMigrationIntro
          ? {
              variant: 'drawer',
              spacious: true,
              contentSx: { pb: `calc(${theme.spacing(2)} - 1px)` },
              sx: { typography: 'titleMedium', cursor: 'default' },
            }
          : undefined
      }
      title={dialogTitle}
    >
      {wizardContent}
    </StdDialog>
  );
};

AccountDiscoveryWizard.propTypes = {
  mode: PropTypes.string,
  initiator: PropTypes.string,
  institutionLogin: PropTypes.object,
  closeOnBack: PropTypes.bool,
  /** Renders the institution search step without a dialog, for hosts that lay it out themselves. */
  embeddedSearch: PropTypes.bool,
  /** Removes the embedded search step's legacy horizontal inset so it aligns to its host. */
  embeddedSearchFullWidth: PropTypes.bool,
};

export default AccountDiscoveryWizard;
